import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/routes/routes';
import type { ReservationHolder } from '@/infrastructure/FulfillmentAdminClient';
import { ADJUST_REASONS, COUNT_ERROR, countInputError, countedDiff, reasonRequiredFor } from '../inventoryView';
import type { AdjustReason, InventoryScreenRow } from '../inventoryView';

/** A save refused because the shelf moved under the editor: what to tell the operator, and the balance it holds now. */
export interface StockAdjustConflict {
  message: string;
  current: number;
}

export interface StockAdjustPopoverProps {
  row: InventoryScreenRow;
  busy: boolean;
  /** `expectedOnHand` is the balance this box was opened against, so the server can refuse a stale save. */
  onApply: (countedQty: number, reason: AdjustReason | null, expectedOnHand: number) => void;
  onCancel: () => void;
  /** Reads the order lines holding this sku on this shelf, so the warning names who goes short. */
  loadHolders: (sku: string, locationCode: string) => Promise<ReservationHolder[]>;
  conflict?: StockAdjustConflict | null;
}

/** Reservations move while the box is open (orders land, get cancelled), so the list is re-read on this beat. */
export const HOLDERS_REFRESH_MS = 15_000;

interface HoldersState {
  /** The last list read; null until a read has landed. */
  holders: ReservationHolder[] | null;
  loading: boolean;
  /** The latest read failed, which says nothing about who holds the sku. */
  failed: boolean;
}

/** How a holder read ended: the box may have closed before it landed, and then nothing may act on it. */
type HoldersRead = { status: 'loaded'; holders: ReservationHolder[] } | { status: 'failed' } | { status: 'closed' };

/** What the below-reserved warning says, so a re-read before saving can tell whether the operator saw the current one. */
const warningOf = (holders: ReservationHolder[] | null, fallbackReserved: number, counted: number | null): string => {
  const reserved = holders ? holders.reduce((sum, holder) => sum + holder.qty, 0) : fallbackReserved;
  if (counted === null || counted >= reserved) {
    return '';
  }
  return [reserved, ...(holders ?? []).map((holder) => `${holder.orderId}/${holder.lineNo}:${holder.qty}`)].join('|');
};

/** Reads the holders on open, on window focus and on a timer while open; the newest read wins. */
const useReservationHolders = (
  sku: string,
  locationCode: string,
  loadHolders: StockAdjustPopoverProps['loadHolders'],
) => {
  const [state, setState] = useState<HoldersState>({ holders: null, loading: true, failed: false });
  const requestRef = useRef(0);
  const latestReadRef = useRef<Promise<HoldersRead> | null>(null);
  const closedRef = useRef(false);
  const loadRef = useRef(loadHolders);
  loadRef.current = loadHolders;

  const refresh = useCallback((): Promise<HoldersRead> => {
    const requestId = ++requestRef.current;
    setState((previous) => ({ ...previous, loading: true }));
    const read = (async (): Promise<HoldersRead> => {
      let outcome: HoldersRead;
      try {
        outcome = { status: 'loaded', holders: await loadRef.current(sku, locationCode) };
      } catch {
        outcome = { status: 'failed' };
      }
      // A read that lands after close must not write into an unmounted box.
      if (closedRef.current) {
        return { status: 'closed' };
      }
      // A newer read started meanwhile (timer, focus), so its answer is the current one.
      if (requestId !== requestRef.current) {
        return latestReadRef.current ?? { status: 'closed' };
      }
      // A failed read keeps the last list on screen and says so; it never reads as "nobody holds this".
      setState((previous) =>
        outcome.status === 'loaded'
          ? { holders: outcome.holders, loading: false, failed: false }
          : { ...previous, loading: false, failed: true },
      );
      return outcome;
    })();
    latestReadRef.current = read;
    return read;
  }, [sku, locationCode]);

  useEffect(() => {
    closedRef.current = false;
    void refresh();
    const timer = window.setInterval(() => void refresh(), HOLDERS_REFRESH_MS);
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      closedRef.current = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  return { ...state, refresh };
};

/**
 * The in-cell box behind a row's Total [19]. An increase can only ever be a miscount, so it is applied
 * without a question; a decrease has four readings and must name one. There is no free-text reason —
 * anything outside the four belongs to a route that already exists (returns, inbound, reservation release).
 * It opens with the number selected for retyping; Enter applies, and Esc or a click outside its cell cancels.
 */
export const StockAdjustPopover = ({
  row,
  busy,
  onApply,
  onCancel,
  loadHolders,
  conflict = null,
}: StockAdjustPopoverProps) => {
  const [value, setValue] = useState(String(row.onHand));
  // The balance the operator started from; a refresh under the open box must not quietly move it.
  const [expectedOnHand, setExpectedOnHand] = useState(row.onHand);
  const [reason, setReason] = useState<AdjustReason | ''>('');
  const [checking, setChecking] = useState(false);
  const [changedBeforeApply, setChangedBeforeApply] = useState(false);
  /** The box was emptied by a conflict, not by the operator: the conflict message alone says why, until they type. */
  const [clearedByConflict, setClearedByConflict] = useState(false);
  const applyingRef = useRef(false);
  const holderState = useReservationHolders(row.sku, row.locationCode, loadHolders);
  const { holders } = holderState;
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const reasonRef = useRef<HTMLSelectElement>(null);
  const dismissRef = useRef({ busy, onCancel });
  dismissRef.current = { busy, onCancel };

  useEffect(() => {
    // The box overhangs its row, so bring the whole box into view rather than only the input the focus would chase.
    inputRef.current?.focus({ preventScroll: true });
    inputRef.current?.select();
    popoverRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, []);

  useEffect(() => {
    // Once the operator has been told the shelf moved, the next apply is made knowingly against the new balance:
    // the typed number goes, so what is applied next was entered against the balance on screen now [SSD-7209].
    if (conflict) {
      setExpectedOnHand(conflict.current);
      setValue('');
      setClearedByConflict(true);
      setReason('');
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [conflict]);

  useEffect(() => {
    // The cell that anchors the box counts as inside: its ✎ already toggles the box, and closing here first would reopen it.
    const dismissOutside = (event: PointerEvent) => {
      const anchor = popoverRef.current?.parentElement ?? popoverRef.current;
      if (!dismissRef.current.busy && anchor && !anchor.contains(event.target as Node)) {
        dismissRef.current.onCancel();
      }
    };
    document.addEventListener('pointerdown', dismissOutside);
    return () => document.removeEventListener('pointerdown', dismissOutside);
  }, []);

  // One parser for the whole screen, so the popover and the audit column accept exactly the same input.
  const parsed = countedDiff(row.onHand, value);
  const valid = parsed !== null;
  const delta = parsed ?? 0;
  const counted = row.onHand + delta;
  const needsReason = valid && reasonRequiredFor(delta);
  // The live holder list outranks the row, which is only as fresh as the page load [SSD-6930 10-6].
  const reserved = holders ? holders.reduce((sum, holder) => sum + holder.qty, 0) : row.reserved;
  const belowReserved = valid && counted < reserved;
  const shortage = reserved - counted;
  const applicable = valid && delta !== 0 && (!needsReason || reason !== '') && !busy && !checking;
  // The audit column reads a blank box as "uncounted"; here a blank box is a number that was deleted, so it says why it cannot apply.
  // A box a conflict emptied is not a bad entry: the conflict message already says what to do, so no second error joins it.
  const inputError = value.trim() === '' ? (clearedByConflict ? null : COUNT_ERROR) : countInputError(value);
  const apply = async () => {
    // Enter and a click can both land before `checking` re-renders, so the guard is a ref.
    if (applyingRef.current) {
      return;
    }
    applyingRef.current = true;
    setChecking(true);
    const shown = warningOf(holders, row.reserved, valid ? counted : null);
    const read = await holderState.refresh();
    applyingRef.current = false;
    setChecking(false);
    if (read.status === 'closed') {
      // Cancelled or dismissed while the re-read was out: the operator no longer asked for this save.
      return;
    }
    // A failed re-read keeps the save open: its message already says the list may be stale.
    if (read.status === 'loaded' && warningOf(read.holders, row.reserved, valid ? counted : null) !== shown) {
      setChangedBeforeApply(true);
      return;
    }
    setChangedBeforeApply(false);
    onApply(counted, needsReason ? (reason as AdjustReason) : null, expectedOnHand);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (!busy) {
        onCancel();
      }
      return;
    }
    if (
      event.key !== 'Enter' ||
      event.nativeEvent.isComposing ||
      (event.target !== inputRef.current && event.target !== reasonRef.current)
    ) {
      return;
    }
    event.preventDefault();
    if (applicable) {
      void apply();
    } else if (needsReason && reason === '') {
      reasonRef.current?.focus();
    }
  };

  return (
    <div className="tot-pop" ref={popoverRef} onKeyDown={onKeyDown}>
      <div className="prow">
        <input
          ref={inputRef}
          className={inputError ? 'tot-in bad' : 'tot-in'}
          aria-label="New quantity"
          aria-invalid={inputError !== null}
          inputMode="numeric"
          value={value}
          disabled={checking}
          onChange={(event) => {
            setValue(event.target.value);
            setClearedByConflict(false);
          }}
        />
        <button
          type="button"
          className="tot-ok"
          aria-label="Apply adjustment"
          disabled={!applicable}
          onClick={() => void apply()}
        >
          ✓
        </button>
        <button type="button" className="tot-no" aria-label="Cancel adjustment" onClick={onCancel}>
          ✕
        </button>
      </div>
      {conflict && (
        <div className="tot-bad" role="alert">
          {conflict.message}
        </div>
      )}
      {inputError && (
        <div className="tot-bad" role="alert">
          {inputError}
        </div>
      )}
      {needsReason && reason === '' && <div className="tot-need">Choose a reason to apply this decrease.</div>}
      {needsReason && (
        <select
          ref={reasonRef}
          className="tot-rsn"
          aria-label="Adjustment reason"
          value={reason}
          disabled={checking}
          onChange={(event) => setReason(event.target.value as AdjustReason)}
        >
          <option value="">Select a reason</option>
          {ADJUST_REASONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      )}
      {changedBeforeApply && (
        <div className="tot-need" role="alert">
          Reservations changed while this was open — check the warning and apply again.
        </div>
      )}
      {belowReserved && (
        <div className="tot-warn" aria-label="Below reserved warning">
          <span className="wh">Below the reserved quantity</span>
          {`${shortage} unit(s) short — ${reserved} reserved against a new total of ${counted}. `}
          Stock that is physically gone must still be recordable, so this is allowed — the holding orders will have to
          be re-purchased.
          {holders && holders.length > 0 && (
            <ul className="tot-holders" aria-label="Holding orders">
              {holders.map((holder) => (
                <li key={`${holder.orderId}-${holder.lineNo}`}>
                  <Link to={ROUTES.fulfillment.orderDetail(holder.orderId)}>
                    #{holder.orderId} / line {holder.lineNo}
                  </Link>
                  {` — ${holder.qty} reserved`}
                </li>
              ))}
            </ul>
          )}
          {holderState.loading && !holders && !holderState.failed && (
            <div className="tot-holders-note">Loading the holding orders…</div>
          )}
          {holderState.failed && (
            <div className="tot-holders-note bad" role="alert">
              {holders
                ? 'Could not refresh the holding orders — this is the last list read and may be out of date.'
                : `Could not load the holding orders — ${row.reserved} reserved is the figure from when the page loaded.`}
            </div>
          )}
        </div>
      )}
      <div className="tot-hint">
        Recorded as a manual adjust on {row.locationCode || 'this sku'}, with its reason and the shelf quantity before
        and after, in Stock History.
      </div>
    </div>
  );
};
